<script setup lang="ts">
import { computed, onMounted, reactive, shallowRef } from 'vue';
import axios from 'axios';
import { ApiError } from '@/utils/errors';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { useRules } from '@/composables/vuetify-validation';
import { Company, CompanyResponse, RawCompanyResponse } from '@/modules/common/models/admin';
import { SelectValues } from '@/types/select';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

enum IntegrationSystem {
  NONE = 0,
  SMARTLOAN = 1,
}

const snackbarManager = useSnackbarManager();

const isLoading = shallowRef(false);
const isSaving = shallowRef(false);

interface IntegrationFormData {
  url: string | null;
  username: string | null;
  password: string | null;
}

const formData = reactive<IntegrationFormData>({
  url: null,
  username: null,
  password: null,
});

const integrationSystemOptions: SelectValues<IntegrationSystem> = [
  { value: IntegrationSystem.NONE, text: 'None' },
  { value: IntegrationSystem.SMARTLOAN, text: 'Smartloan' },
];

const integrationSystem = shallowRef<IntegrationSystem>(IntegrationSystem.NONE);

const showSmartLoanFields = computed(() => integrationSystem.value === IntegrationSystem.SMARTLOAN);

const { rules, validate, resetValidation } = useRules<{
  smartLoanIntegrationUrl: string;
  smartLoanIntegrationUsername: string;
  smartLoanIntegrationPassword: string;
}>({
  smartLoanIntegrationUrl: {
    rules: [
      (v) => integrationSystem.value === IntegrationSystem.NONE || !!v || 'Please enter a value',
    ],
  },
  smartLoanIntegrationUsername: {
    rules: [
      (v) => integrationSystem.value === IntegrationSystem.NONE || !!v || 'Please enter a value',
    ],
  },
  smartLoanIntegrationPassword: {
    rules: [
      (v) => integrationSystem.value === IntegrationSystem.NONE || !!v || 'Please enter a value',
    ],
  },
});

const companyPreferences = shallowRef<Company | null>(null);

async function loadData(): Promise<void> {
  isLoading.value = true;

  try {
    const { data } = await axios.get<RawCompanyResponse>('/api/1/trader-admin/company');
    companyPreferences.value = CompanyResponse.fromData(data).company;

    if (!companyPreferences.value) {
      throw new Error('Company preferences not found');
    }

    integrationSystem.value = companyPreferences.value.smartLoanIntegrationUrl
      ? IntegrationSystem.SMARTLOAN
      : IntegrationSystem.NONE;

    formData.url = companyPreferences.value.smartLoanIntegrationUrl;
    formData.username = companyPreferences.value.smartLoanIntegrationUsername;
    formData.password = null;

    resetValidation();
  } catch (err) {
    snackbarManager.show({
      message: new ApiError(i18nServerMessage(err as Error)).message,
      color: 'error',
    });
  } finally {
    isLoading.value = false;
  }
}

async function saveData(): Promise<void> {
  if (!(await validate()) || isSaving.value) return;

  isSaving.value = true;

  try {
    const payload =
      integrationSystem.value === IntegrationSystem.SMARTLOAN
        ? {
            ...companyPreferences.value,
            smartLoanIntegrationUrl: formData.url,
            smartLoanIntegrationUsername: formData.username,
            smartLoanIntegrationPassword: formData.password,
          }
        : {
            ...companyPreferences.value,
            smartLoanIntegrationUrl: null,
            smartLoanIntegrationUsername: null,
            smartLoanIntegrationPassword: null,
          };

    await axios.put('/api/1/trader-admin/company', {
      // because we don't have a separate endpoint just for smartloan integration,
      //  we need to send the whole company preferences so they are preserved
      ...companyPreferences.value,
      ...payload,
    });

    snackbarManager.show({
      message: 'Integration settings have been updated.',
      color: 'success',
    });

    await loadData();
  } catch (err) {
    snackbarManager.show({
      message: new ApiError(i18nServerMessage(err as Error)).message,
      color: 'error',
    });
  } finally {
    isSaving.value = false;
  }
}

onMounted(() => {
  void loadData();
});
</script>

<template>
  <VCard class="flex d-flex flex-column">
    <VForm novalidate @submit.prevent>
      <VCardTitle class="mb-4">
        <span class="headline">Marketplace External Vendor Integration</span>
      </VCardTitle>

      <VCardText class="text--primary">
        <div v-if="isLoading" class="text-center">
          <VProgressCircular color="primary" indeterminate />
          <div class="mt-2">Loading integration settings...</div>
        </div>

        <template v-else>
          <VRow align="center" dense>
            <VCol cols="4">System</VCol>
            <VCol cols="4">
              <VSelect
                v-model="integrationSystem"
                :disabled="isLoading || isSaving"
                :items="integrationSystemOptions"
              />
            </VCol>
          </VRow>

          <template v-if="showSmartLoanFields">
            <VRow align="center" dense>
              <VCol cols="4">Url</VCol>
              <VCol cols="4">
                <VTextField
                  v-model="formData.url"
                  v-bind="rules.smartLoanIntegrationUrl"
                  :disabled="isLoading || isSaving"
                  placeholder="http://example.com"
                />
              </VCol>
            </VRow>
            <VRow align="center" dense>
              <VCol cols="4">Username</VCol>
              <VCol cols="4">
                <VTextField
                  v-model="formData.username"
                  v-bind="rules.smartLoanIntegrationUsername"
                  :disabled="isLoading || isSaving"
                />
              </VCol>
            </VRow>
            <VRow align="center" dense>
              <VCol cols="4">Password</VCol>
              <VCol cols="4">
                <VTextField
                  v-model="formData.password"
                  v-bind="rules.smartLoanIntegrationPassword"
                  :disabled="isLoading || isSaving"
                />
              </VCol>
            </VRow>
          </template>
        </template>
      </VCardText>

      <VCardActions class="pa-6 justify-end">
        <VBtn
          class="px-6"
          color="primary"
          :disabled="isLoading || isSaving"
          type="submit"
          @click="saveData()"
        >
          <VProgressCircular v-if="isSaving" class="mr-2" indeterminate size="20" />
          Save
        </VBtn>
      </VCardActions>
    </VForm>
  </VCard>
</template>

<style lang="scss" scoped></style>
